<template>
  <v-card>
    <v-card-title>
      <h2 class="card-title">{{ 'base.settings' | translate }}</h2>
    </v-card-title>
    <v-card-content>
      <h3 class="card-subtitle mb-1">{{ 'base.client_info' | translate }}</h3>
      <div class="cols-x">
        <div class="cols-x__2 cols-x__2--offset-md mb-3">
          <v-text-field
            :focus="true"
            :label="$t('base.first_name')"
            v-model="form.first_name"
          />
        </div>
        <div class="cols-x__2 cols-x__2--offset-md mb-3">
          <v-text-field
            :label="$t('base.email')"
            v-model="form.email"
          />
        </div>
      </div>
      <div class="cols-x">
        <div class="cols-x__2 cols-x__2--offset-md mb-3">
          <v-text-field
            :label="$t('base.last_name')"
            v-model="form.last_name"
          />
        </div>
        <div class="cols-x__2 cols-x__2--offset-md mb-3">
          <v-text-field
            :label="$t('base.phone')"
            v-model="form.phone"
          />
        </div>
      </div>
      <div class="cols-x">
        <div class="cols-x__2 cols-x__2--offset-md mb-3">
          <v-text-field
            :label="$t('base.company_name')"
            v-model="form.company_name"
          />
        </div>
        <div class="cols-x__2 cols-x__2--offset-md mb-3">
          <v-text-field
            :label="$t('base.password')"
            v-model="form.password"
          />
        </div>
      </div>
      <div class="cols-x">
        <div class="cols-x__2 cols-x__2--offset-md mb-3 remove-margin-above-mobile">
          <v-text-field
            :label="$t('base.about_client')"
            v-model="form.profile.about"
          />
        </div>
        <div class="cols-x__2 cols-x__2--offset-md">
          <v-select
            item-value="key"
            :label="$t('base.select_lang')"
            :options="languages"
            v-model="form.profile.language"
          />
        </div>
      </div>
    </v-card-content>
    <v-divider secondary />
    
    <v-card-content>
      <h3 class="card-subtitle mb-1">{{ 'base.profeed_info' | translate }}</h3>
      <div class="cols-x">
        <div class="cols-x__2 cols-x__2--offset-md mb-3">
          <v-multiselect-with-api-search
            ref="clientSearch"
            :label="`${$t('base.clients')} (${clients_ids.length}/${clientList.length})`"
            :options="clientList"
            item-name="display_name"
            item-subname="name"
            v-model="clients_ids"
            :loading="clientsLoading"
            :comparator="clientComparator"
            @search="onClientSearch"
            @scroll-complete="onClientScrollComplete"
          />
        </div>
        <div class="cols-x__2 cols-x__2--offset-md mb-3">
          <v-multiselect-with-api-search
            ref="farmSearch"
            :label="`${$t('base.farms')} (${form.farms_ids.length}/${filteredFarms.length})`"
            :options="filteredFarms"
            item-name="name"
            item-subname="name"
            v-model="form.farms_ids"
            :loading="farmsLoading"
            :comparator="farmComparator"
            @search="onFarmSearch"
            @scroll-complete="onFarmScrollComplete"
            :disabled="!clients_ids.length"
          />
        </div>
      </div>
    </v-card-content>
    <v-card-actions>
      <v-btn
        :loading="loading"
        @click="onUpdate"
      >
        {{ 'base.save' | translate }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'SettingsConsultantModal',

  props: {
    item: Object
  },

  data: () => ({
    form: {
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      phone: '',
      user_type: '',
      company_name: '',
      farms_ids: [],
      profile: {
        about: '',
        language: null
      }
    },
    clients_ids: [],
    currentSearchTerm: '',
    currentFarmSearchTerm: '',
    languages: [
      { id: 0, name: 'English', key: 'en' },
      { id: 1, name: 'Українська', key: 'uk' },
      { id: 2, name: 'Русский', key: 'ru' },
      { id: 3, name: 'Polish', key: 'pl' },
      { id: 4, name: 'Nederlands', key: 'nl' },
      { id: 5, name: 'Deutschland', key: 'de' },
      { id: 6, name: 'France', key: 'fr' },
      { id: 7, name: 'Italia', key: 'it' },
      { id: 8, name: 'România', key: 'ro' },
      { id: 9, name: 'USA', key: 'us' },
    ],
    loading: false,
    clientsLoading: false,
    farmsLoading: false,
    hasMoreClients: true,
    hasMoreFarms: true,
    isInitialLoad: true,
    limit: 1000
  }),

  computed: {
    ...mapGetters('dashboard', [
      'clientList',
      'clientsFarms',
      'currentUser'
    ]),

    filteredFarms () {
      if (!this.clients_ids.length) {
        return []
      }

      const selectedClientIds = this.clients_ids.map(client => client.id)

      return this.clientsFarms.filter(farm => {
        return selectedClientIds.includes(farm.client)
      })
    }
  },

  async mounted () {
    this.loadData()

    await this.loadInitialClients()
    this.isInitialLoad = false
  },

  methods: {
    clientComparator (selected, option) {
      return selected.id === option.id
    },

    farmComparator (selected, option) {
      return selected.id === option.id
    },

    async loadInitialClients () {
      const params = {limit: this.limit}
      const result = await this.loadClientList(params)

      if (result && result.next) {
        this.hasMoreClients = true
      } else {
        this.hasMoreClients = false
      }

      if (this.clients_ids.length) {
        await this.loadFarms()
      }
    },

    async onClientSearch (searchTerm) {
      this.clientsLoading = true
      this.currentSearchTerm = searchTerm
      const params = searchTerm ? { name: searchTerm, limit: this.limit } : {}
      const result = await this.loadClientList(params)

      if (result && result.next) {
        this.hasMoreClients = true
      } else {
        this.hasMoreClients = false
      }

      this.clientsLoading = false
    },

    async onClientScrollComplete () {
      if (!this.hasMoreClients || this.clientsLoading) {
        return
      }

      this.clientsLoading = true

      const currentLength = this.clientList.length
      const params = {
        offset: currentLength,
        limit: this.limit
      }

      if (this.currentSearchTerm) {
        params.name = this.currentSearchTerm
      }

      try {
        const result = await this.loadClientList(params)

        if (!result || !result.data || result.data.length === 0 || !result.next) {
          this.hasMoreClients = false
        }
      } catch (error) {
        console.error('Error loading clients:', error)
        this.hasMoreClients = false
      }

      this.clientsLoading = false
    },

    async onFarmSearch (searchTerm) {
      if (!this.clients_ids.length) {
        return
      }

      this.farmsLoading = true
      this.currentFarmSearchTerm = searchTerm

      const clientIds = this.clients_ids.map(client => client.id)
      const params = {
        client: clientIds,
        limit: this.limit
      }

      if (searchTerm) {
        params.name = searchTerm
      }

      try {
        const result = await this.loadClientsFarms(params)

        if (result && result.next) {
          this.hasMoreFarms = true
        } else {
          this.hasMoreFarms = false
        }
      } catch (error) {
        console.error('Error searching farms:', error)
        this.hasMoreFarms = false
      }

      this.farmsLoading = false
    },

    async onFarmScrollComplete () {
      if (!this.hasMoreFarms || this.farmsLoading || !this.clients_ids.length) {
        return
      }

      this.farmsLoading = true

      const currentLength = this.clientsFarms.length
      const clientIds = this.clients_ids.map(client => client.id)
      const params = {
        client: clientIds,
        offset: currentLength,
        limit: this.limit
      }

      if (this.currentFarmSearchTerm) {
        params.name = this.currentFarmSearchTerm
      }

      try {
        const result = await this.loadClientsFarms(params)

        if (!result || !result.data || result.data.length === 0 || !result.next) {
          this.hasMoreFarms = false
        }
      } catch (error) {
        console.error('Error loading farms:', error)
        this.hasMoreFarms = false
      }

      this.farmsLoading = false
    },

    async loadFarms () {
      if (!this.clients_ids.length) {
        this.form.farms_ids = []
        this.hasMoreFarms = false
        return
      }

      const clientIds = this.clients_ids.map(client => client.id)
      const params = {
        client: clientIds,
        limit: this.limit
      }

      try {
        this.farmsLoading = true
        const result = await this.loadClientsFarms(params)

        if (result && result.next) {
          this.hasMoreFarms = true
        } else {
          this.hasMoreFarms = false
        }
      } catch (error) {
        console.error('Error loading farms:', error)
        this.$toasted.show('Error loading farms!')
        this.hasMoreFarms = false
      } finally {
        this.farmsLoading = false
      }
    },

    removeFarmsOfUncheckedClients (oldClients, newClients) {
      const oldClientIds = oldClients.map(client => client.id)
      const newClientIds = newClients.map(client => client.id)
      const uncheckedClientIds = oldClientIds.filter(id => !newClientIds.includes(id))

      const farmToClientMap = {}
      this.clientsFarms.forEach(apiFarm => {
        farmToClientMap[apiFarm.id] = apiFarm.client || apiFarm.client_id || apiFarm.owner || apiFarm.owner_id
      })

      this.form.farms_ids = this.form.farms_ids.filter(farm => {
        const clientId = farmToClientMap[farm.id]
        return !uncheckedClientIds.includes(clientId)
      })
    },

    filterFarmsBySelectedClients () {
      if (!this.clients_ids.length) {
        this.form.farms_ids = []
        this.hasMoreFarms = false
        return
      }

      const selectedClientIds = this.clients_ids.map(client => client.id)
      this.form.farms_ids = this.form.farms_ids.filter(farm => {
        return selectedClientIds.includes(farm.client)
      })
    },

    loadData () {
      this.form.user_type = this.currentUser.user_type
      this.form.first_name = this.currentUser.first_name
      this.form.last_name = this.currentUser.last_name
      this.form.phone = this.currentUser.phone
      this.form.email = this.currentUser.email
      this.form.company_name = this.currentUser.company_name || ''
      this.form.profile.language = this.currentUser.profile.language
      this.form.profile.about = this.currentUser.profile.about || ''
      this.clients_ids = this.currentUser.clients || []
      this.form.farms_ids = this.currentUser.farms || []
    },

    ...mapActions('dashboard', [
      'loadClientList',
      'loadClientsFarms',
      'updateUser'
    ]),

    async onUpdate () {
      const payload = {
        id: this.currentUser.id,
        email: this.form.email,
        password: this.form.password,
        first_name: this.form.first_name,
        last_name: this.form.last_name,
        phone: this.form.phone,
        user_type: this.form.user_type,
        company_name: this.form.company_name,
        profile: {
          about: this.form.profile.about,
          language: this.form.profile.language,
        },
        clients: this.clients_ids.map(item => ({ id: item.id })),
        farms: this.form.farms_ids.map(item => ({ id: item.id })),
      }

      this.loading = true
      const errors = await this.updateUser(payload)
      this.loading = false
      if (!errors) {
        this.$emit('close')
      } else {
        if (errors.email) {
          this.$toasted.show('Incorrect email!')
        } else {
          if (errors.phone) {
            this.$toasted.show('Incorrect phone!')
          } else {
            this.$toasted.show('Something went wrong!')
          }
        }
      }
    }
  },

  watch: {
    clients_ids: {
      handler (newClients, oldClients) {
        if (this.isInitialLoad) {
          return
        }

        if (!newClients.length) {
          this.form.farms_ids = []
          this.hasMoreFarms = false
          return
        }

        if (oldClients && oldClients.length > newClients.length) {
          this.removeFarmsOfUncheckedClients(oldClients, newClients)
        }

        this.loadFarms()
      },
      deep: true
    }
  }
}
</script>